<script lang="ts">
  import type { Weapon } from '../data.js';
  import { i18next } from '../i18n.js';
  import { lang as langState } from '../lang-state.svelte.js';
  import { poolForWeapon } from '../data/weapon-attrs.js';

  interface Props {
    weapon: Weapon | null;
    selected: string | null;
    onChange: (stat: string | null) => void;
  }

  let { weapon, selected, onChange }: Props = $props();
  let lang = $derived(langState.current);

  let intrinsic = $derived(weapon?.intrinsicAttrs ?? []);
  let hasFull = $derived(intrinsic.length >= 3);
  let isExotic = $derived(weapon?.kind === 'exotic');
  let pool = $derived(poolForWeapon(weapon));
  let defaultStat = $derived(hasFull ? intrinsic[2].stat : null);
  let activeStat = $derived(selected ?? defaultStat);
  let activeValue = $derived.by(() => {
    if (!hasFull) return 0;
    if (!selected) return intrinsic[2].value;
    const hit = pool.find((p) => p.stat === selected);
    return hit ? hit.max : intrinsic[2].value;
  });

  function statName(s: string): string {
    void lang;
    return i18next.t(s, { ns: 'stats', defaultValue: s }) as string;
  }
</script>

{#if weapon && intrinsic.length > 0}
  <section class="panel wap">
    <div class="panel-title">
      <span>{lang === 'ru' ? 'Атрибуты оружия' : 'Weapon attributes'}</span>
    </div>

    {#each intrinsic.slice(0, 2) as a (a.stat)}
      <div class="row fixed" title={lang === 'ru' ? 'Фиксированный атрибут оружия' : 'Fixed weapon attribute'}>
        <span class="lock">🔒</span>
        <span class="name">{statName(a.stat)}</span>
        <span class="val">+{a.value}%</span>
      </div>
    {/each}

    {#if hasFull}
      <div class="row pick">
        <span class="ico">⚙</span>
        <select
          class="input"
          value={selected ?? ''}
          onchange={(e) => {
            const v = (e.currentTarget as HTMLSelectElement).value;
            onChange(v === '' ? null : v);
          }}
        >
          <option value="">
            {lang === 'ru' ? 'По умолчанию' : 'Default'}: {statName(intrinsic[2].stat)} (+{intrinsic[2].value}%)
          </option>
          {#each pool as p (p.stat)}
            <option value={p.stat}>{statName(p.stat)} (+{p.max}%)</option>
          {/each}
        </select>
        <span class="val">+{activeValue}%</span>
      </div>
      <div class="hint">
        {#if isExotic}
          {lang === 'ru'
            ? '🔒 Слоты 1 и 2 фиксированы. Слот 3 — выбор игрока (recalibration на крафтстанции). Применённый: '
            : "🔒 Slots 1 & 2 are fixed. Slot 3 is player-picked (recalibration at the crafting bench). Applied: "}
        {:else}
          {lang === 'ru'
            ? '3-й слот выбирается. Применённый стат: '
            : '3rd slot is user-pickable. Applied stat: '}
        {/if}
        <b>{statName(activeStat ?? '')}</b>
      </div>
    {:else}
      <div class="hint dim">
        {lang === 'ru'
          ? 'Полный набор из 3 атрибутов появится после backfill data.'
          : 'Full 3-attribute set appears after data backfill.'}
      </div>
    {/if}
  </section>
{/if}

<style>
  .wap {
    background: var(--bg-2);
    border: 1px solid var(--border);
    border-left: 3px solid var(--blue);
    border-radius: var(--r);
    padding: 8px 10px;
    margin-top: 8px;
  }
  .row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 8px;
    align-items: center;
    padding: 4px 0;
    font-size: 12px;
  }
  .row + .row { border-top: 1px dashed var(--border); }
  .lock { color: var(--muted); font-size: 10px; }
  .ico { color: var(--blue); font-size: 12px; }
  .name { font-family: var(--f-mono); color: var(--text); }
  .val { color: var(--green); font-family: var(--f-mono); font-weight: 600; }
  .input { padding: 4px 8px; font-size: 12px; width: 100%; }
  .pick { padding-top: 6px; }
  .hint {
    margin-top: 6px;
    font-size: 10px;
    color: var(--text-dim);
    line-height: 1.4;
  }
  .hint.dim { color: var(--muted); font-style: italic; }
</style>
